import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import { noop } from 'lodash';

import { FormFieldError, Label } from 'shared/components';
import { ASTERISK, OPTIONAL, REQUIRED } from 'shared/components/_constants/label';
import requestsStatuses from 'modules/common/constants/requestsStatuses';

import theme from './Textarea.scss';

class Textarea extends PureComponent {

  static propTypes = {
    testId: PropTypes.string.isRequired,
    intl: PropTypes.object.isRequired,
    isControlled: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    showLengthCounter: PropTypes.bool,
    defaultValue: PropTypes.string,
    className: PropTypes.string,
    rootClassName: PropTypes.string,
    label: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
    ]),
    labelSuffix: PropTypes.oneOfType([
      PropTypes.oneOf([ASTERISK, OPTIONAL, REQUIRED]),
      PropTypes.node,
    ]),
    placeholder: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
    ]),
    status: PropTypes.oneOf([
      requestsStatuses.default,
      requestsStatuses.failure,
      requestsStatuses.pending,
      requestsStatuses.success,
    ]),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    mediaResized: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    tooltipType: PropTypes.oneOf(['text', 'hint', 'intl']),
    tooltipTestId: PropTypes.string,
    tooltipMessage: PropTypes.object,
    tooltipSize: PropTypes.oneOf(['medium', 'large']),
    tooltipPlacement: PropTypes.string,
    isMobile: PropTypes.bool,
  };

  static defaultProps = {
    isControlled: true,
    name: null,
    value: null,
    defaultValue: '',
    minLength: 0,
    maxLength: null,
    showLengthCounter: false,
    className: '',
    rootClassName: '',
    label: null,
    labelSuffix: null,
    placeholder: '',
    status: requestsStatuses.default,
    size: 'small',
    mediaResized: false,
    required: false,
    disabled: false,
    onFocus: noop,
    onBlur: noop,

    tooltipType: undefined,
    tooltipTestId: 'tooltip',
    tooltipMessage: null,
    tooltipSize: 'large',
    tooltipPlacement: 'bottomStart',
    isMobile: false,
  };

  state = {
    focused: false,
    value: null,
  };

  handleChange = (e) => {
    const { onChange, isControlled } = this.props;
    const value = e.target.value || '';

    if (!isControlled) this.setState({ value });
    onChange(value, e);
  };

  handleFocus = (e) => {
    this.setState({ focused: true });

    if (this.textarea) {
      this.textarea.focus();
    }

    this.props.onFocus(e);
  };

  handleBlur = (e) => {
    this.setState({ focused: false });

    this.props.onBlur(e);
  };

  fieldRenderer() {
    const {
      testId,
      className,
      rootClassName,
      placeholder,
      status,
      size,
      mediaResized,
      disabled,
      required,
      intl,
      name,
      value,
      defaultValue,
      minLength,
      maxLength,
      showLengthCounter,
      tooltipType,
      tooltipTestId,
      tooltipMessage,
      tooltipSize,
      tooltipPlacement,
      isMobile,
    } = this.props;

    const { focused, value: stateValue } = this.state;

    const textareaProps = {
      name,
      required,
      disabled,
      minLength,
      maxLength,
      className: classnames(theme.root, className),
      placeholder: placeholder
        ? typeof (placeholder) === 'string' ? placeholder : intl.formatMessage({ ...placeholder })
        : null,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      value: stateValue !== null ? stateValue : value || defaultValue,
      ref: (ref) => this.textarea = ref,
    };

    const rootClasses = classnames({
      [theme.disabled]: disabled,
      [theme.inputFocused]: focused,
      [theme.mediaResized]: mediaResized,
      [theme[status.toLowerCase()]]: status.toLowerCase(),
      [theme[size]]: size,
    }, theme.root, rootClassName);

    return (
      <div className={ rootClasses }>
        <textarea
          data-testid={ testId }
          { ...textareaProps }
        />

        <If condition={ showLengthCounter && maxLength }>
          <div className={ theme.lengthCounter }>
            { isMobile ? (
              <FormattedMessage
                id="NewConsultationWizard.MessagingConsultation.RequestTextInput.MaxCharacters.Label"
                defaultMessage="** Characters limit: { characters }"
                values={{ characters: maxLength }}
              />
            ) : (
              <FormattedMessage
                id="General.Textarea.CharactersRemaining"
                defaultMessage="** Characters remaining: { characters }"
                values={{ characters: maxLength - [...value].length }}
              />
            ) }
          </div>
        </If>

        <FormFieldError
          tooltipType={ tooltipType }
          testId={ tooltipTestId }
          tooltipMessage={ tooltipMessage }
          tooltipSize={ tooltipSize }
          tooltipPlacement={ tooltipPlacement }
        />

      </div>
    );
  }

  render() {
    const { label, labelSuffix } = this.props;

    if (label) {
      return (
        <div>
          <Label
            label={ label }
            className={ theme.label }
            labelSuffix={ labelSuffix }
          />

          { this.fieldRenderer() }
        </div>
      );
    }

    return this.fieldRenderer();
  }
}

export default injectIntl(Textarea);
